import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../utils/authFetch";
import usePolling from './usePolling';
import { POLLING_INTERVALS } from '../config/polling';

/**
 * useProblemData - Custom hook for fetching problem data
 *
 * Extracts common problem data fetching logic used by ImpactViewPage
 *
 * Features:
 * - Initial load with full loading state
 * - Optional background auto-refresh (every 2 minutes)
 * - Separate refreshing state for subtle UI updates
 * - Preserves data during background refresh
 *
 * @param {string} problemId - The problem ID to fetch
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoRefresh - Enable auto-refresh every 2 minutes (default: false)
 * @param {function} options.shouldRefresh - Function that returns boolean to control refresh timing
 * @returns {Object} - { loading, refreshing, error, problemData, lastUpdated, refetch }
 */
function useProblemData(problemId, options = {}) {
  const { autoRefresh = false, shouldRefresh = () => true } = options;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [problemData, setProblemData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchProblemData = useCallback(async (isBackgroundRefresh = false) => {
    if (!problemId) {
      setLoading(false);
      return;
    }

    // Set appropriate loading state
    if (isBackgroundRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // Fetch the problem data from the API
      const response = await authFetch(`/api/v1/problem/${problemId}?mode=viewer`);

      if (!response.ok) {
        throw new Error(`Failed to load problem: ${response.statusText}`);
      }

      const data = await response.json();
      const prb = data.problem;

      // Extract basic problem information needed for impact features
      if (prb.outages && prb.outages["OUTAGE 1"]) {
        const currentOutage = prb.outages["OUTAGE 1"];
        const payload = {
          number: prb.number,
          short_description: currentOutage.short_description,
          priority: currentOutage.priority,
          temporary: prb.temporary || false,
        };

        setProblemData(payload);
        setLastUpdated(new Date());
        setError(null); // Clear error on success
      }

      setIsInitialLoad(false);
    } catch (err) {
      console.error("Error fetching problem data:", err);

      // On background refresh error, keep old data
      if (isBackgroundRefresh) {
        console.warn('Background refresh failed, keeping existing data:', err);
      } else {
        // On initial load error, show error
        setError(err.message || "Failed to load problem data");
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [problemId]);

  // Initial fetch on mount
  useEffect(() => {
    fetchProblemData(false);
  }, [fetchProblemData]);

  // Background auto-refresh using reusable polling hook
  usePolling(
    () => {
      if (shouldRefresh()) {
        fetchProblemData(true);
      }
    },
    POLLING_INTERVALS.PROBLEM_DATA,
    {
      enabled: autoRefresh && !isInitialLoad,
      pauseWhenHidden: true
    }
  );

  return {
    loading,
    refreshing,
    error,
    problemData,
    lastUpdated,
    refetch: () => fetchProblemData(true),
  };
}

export default useProblemData;
